import { useState, useRef } from "react";
import api from "@/lib/api";
import { fmtDuration } from "@/lib/format";
import { MapPin, ArrowUp, ArrowDown, X, MagnifyingGlass, DotsSixVertical, User } from "@phosphor-icons/react";
import { toast } from "sonner";

const STAGE_TYPES = ["Depot", "Collection", "Delivery", "Supplier", "Waypoint", "Return"];
const TYPE_COLOR = {
  depot: "#0f172a", collection: "#0d9488", delivery: "#2563eb",
  supplier: "#d97706", waypoint: "#7c3aed", return: "#64748b",
};

const hhmm = (iso) => iso ? new Date(iso).toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Africa/Johannesburg" }) : "--:--";

export default function StageRow({ index, stage, leg, schedule, isFirst, startTime, onStartTime,
  contacts, durations, onType, onChange, onLegChange, onMove, onRemove, canRemove }) {
  const [contactOpen, setContactOpen] = useState(false);
  const [addrResults, setAddrResults] = useState([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const blurT = useRef(null);
  const addrBlurT = useRef(null);

  const color = TYPE_COLOR[stage.type] || "#64748b";
  const letter = String.fromCharCode(65 + index);

  const filtered = contacts.filter((c) => {
    if (!stage.name) return true;
    const q = stage.name.toLowerCase();
    return c.name.toLowerCase().includes(q) || (c.company || "").toLowerCase().includes(q);
  }).slice(0, 8);

  const pick = (c) => {
    onChange({ contact_id: c.id, name: c.name, address: c.address || "", lat: c.lat ?? null, lng: c.lng ?? null });
    setContactOpen(false);
  };

  const searchAddress = async () => {
    if (!stage.address) { toast.error("Type an address to search"); return; }
    setSearching(true);
    try {
      const { data } = await api.post("/routing/geocode", { query: stage.address });
      setAddrResults(data.results || []);
      setAddrOpen(true);
      if (!data.results?.length) toast.error("No location match found");
    } finally { setSearching(false); }
  };

  return (
    <div className="relative rounded-lg border border-slate-200 bg-white overflow-visible" data-testid={`stage-${index}`}
      style={{ borderLeft: `4px solid ${color}` }}>
      <div className="flex gap-3 p-3">
        {/* handle + letter + controls */}
        <div className="flex flex-col items-center gap-1 pt-1">
          <DotsSixVertical size={16} className="text-slate-300" />
          <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: color }}>{letter}</div>
          <div className="flex flex-col">
            <button type="button" onClick={() => onMove(-1)} className="text-slate-300 hover:text-slate-700"><ArrowUp size={14} /></button>
            <button type="button" onClick={() => onMove(1)} className="text-slate-300 hover:text-slate-700"><ArrowDown size={14} /></button>
          </div>
        </div>

        <div className="flex-1 min-w-0 space-y-2">
          {/* row 1: type / contact / time */}
          <div className="grid grid-cols-1 sm:grid-cols-[150px_1fr_120px] gap-2">
            <div>
              <Lbl>Stage type</Lbl>
              <select className="cd-input capitalize" value={stage.type} onChange={(e) => onType(e.target.value)} data-testid={`stage-type-${index}`}>
                {STAGE_TYPES.map((t) => <option key={t} value={t.toLowerCase()}>{t}</option>)}
              </select>
            </div>
            <div className="relative">
              <Lbl>Saved contact / point</Lbl>
              <div className="relative">
                <User size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input className="cd-input pl-8" placeholder="Search contacts or type a name…" value={stage.name || ""}
                  data-testid={`stage-contact-input-${index}`}
                  onChange={(e) => onChange({ name: e.target.value, contact_id: null })}
                  onFocus={() => { clearTimeout(blurT.current); setContactOpen(true); }}
                  onBlur={() => { blurT.current = setTimeout(() => setContactOpen(false), 160); }} />
                {stage.contact_id && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-green-600 font-medium">saved</span>}
              </div>
              {contactOpen && filtered.length > 0 && (
                <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto cd-scroll">
                  {filtered.map((c) => (
                    <button key={c.id} type="button" onMouseDown={(e) => { e.preventDefault(); pick(c); }}
                      data-testid={`stage-contact-option-${index}-${c.id}`}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between">
                      <div><div className="text-sm font-medium text-slate-900">{c.name}</div><div className="text-[11px] text-slate-400">{c.company || c.address || "—"}</div></div>
                      <span className="text-[10px] uppercase tracking-wider text-slate-400 capitalize">{c.type}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Lbl>{isFirst ? "Start time" : "Arrival"}</Lbl>
              {isFirst ? (
                <input type="time" className="cd-input" value={startTime} onChange={(e) => onStartTime(e.target.value)} data-testid={`stage-start-${index}`} />
              ) : (
                <div className="cd-input bg-slate-50 text-slate-600 font-mono flex items-center" data-testid={`stage-arrival-${index}`}>{hhmm(schedule?.arrival)}</div>
              )}
            </div>
          </div>

          {/* row 2: location name / street address / leg km */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1.4fr_110px] gap-2">
            <div>
              <Lbl>Location name</Lbl>
              <input className="cd-input" placeholder="e.g. CargoDash — Pomona" value={stage.name || ""} onChange={(e) => onChange({ name: e.target.value })} data-testid={`stage-locname-${index}`} />
            </div>
            <div className="relative">
              <Lbl>Street address</Lbl>
              <div className="flex gap-1">
                <input className="cd-input flex-1" placeholder="Type address, then search the map" value={stage.address || ""}
                  data-testid={`stage-address-${index}`}
                  onChange={(e) => onChange({ address: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchAddress(); } }}
                  onFocus={() => clearTimeout(addrBlurT.current)}
                  onBlur={() => { addrBlurT.current = setTimeout(() => setAddrOpen(false), 180); }} />
                <button type="button" onClick={searchAddress} disabled={searching}
                  className={`px-2.5 rounded-md border ${stage.lat ? "border-green-300 text-green-600" : "border-slate-300 text-slate-500"} hover:bg-slate-50`}
                  title="Search map" data-testid={`stage-address-search-${index}`}>
                  {searching ? <span className="text-xs">…</span> : <MagnifyingGlass size={15} />}
                </button>
              </div>
              {addrOpen && addrResults.length > 0 && (
                <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-56 overflow-y-auto cd-scroll">
                  {addrResults.map((r, ri) => (
                    <button key={ri} type="button" onMouseDown={(e) => { e.preventDefault(); onChange({ address: r.label, lat: r.lat, lng: r.lng }); setAddrOpen(false); }}
                      data-testid={`stage-address-option-${index}-${ri}`}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm text-slate-700 flex items-start gap-2">
                      <MapPin size={14} className="text-slate-400 mt-0.5 shrink-0" /><span>{r.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Lbl>{isFirst ? "—" : "Leg km"}</Lbl>
              {isFirst ? <div className="cd-input bg-slate-50 text-slate-300">start</div> : (
                <input className="cd-input data-num" value={leg?.distance_km ?? "0"} onChange={(e) => onLegChange({ distance_km: e.target.value, source: "manual" })} data-testid={`leg-km-${index}`} />
              )}
            </div>
          </div>

          {/* row 3: service duration + drive time + loaded + chips */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-1">
            <div className="flex items-center gap-2">
              <Lbl inline>Loading / offloading</Lbl>
              <select className="cd-input py-1 w-28" value={stage.service_minutes} onChange={(e) => onChange({ service_minutes: Number(e.target.value) })} data-testid={`stage-service-${index}`}>
                {durations.map((d) => <option key={d} value={d}>{d === 0 ? "No time" : fmtDuration(d)}</option>)}
              </select>
            </div>
            {!isFirst && (
              <>
                <div className="flex items-center gap-2">
                  <Lbl inline>Drive</Lbl>
                  <input className="cd-input data-num py-1 w-20" value={leg?.drive_minutes ?? 0} onChange={(e) => onLegChange({ drive_minutes: e.target.value, source: "manual" })} data-testid={`leg-min-${index}`} />
                  <span className="text-[11px] text-slate-400">min</span>
                </div>
                <label className="flex items-center gap-1.5 text-xs text-slate-600">
                  <input type="checkbox" className="accent-teal-600" checked={leg?.loaded ?? true} onChange={(e) => onLegChange({ loaded: e.target.checked })} data-testid={`leg-loaded-${index}`} /> loaded
                </label>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${leg?.source === "osrm" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-500"}`}>{leg?.source || "manual"}</span>
              </>
            )}
            <div className="ml-auto flex items-center gap-1.5 text-xs">
              <span className="px-2 py-1 rounded bg-slate-100 text-slate-600 font-mono" data-testid={`chip-arrive-${index}`}>Arrive {hhmm(schedule?.arrival)}</span>
              <span className="px-2 py-1 rounded bg-teal-50 text-teal-700 font-mono" data-testid={`chip-depart-${index}`}>Depart {hhmm(schedule?.departure)}</span>
            </div>
          </div>
        </div>

        <button type="button" onClick={onRemove} disabled={!canRemove} className="text-slate-300 hover:text-red-600 disabled:opacity-30 h-max" title="Remove" data-testid={`stage-remove-${index}`}><X size={18} /></button>
      </div>
    </div>
  );
}

function Lbl({ children, inline }) {
  return <label className={`text-[10px] font-semibold uppercase tracking-wider text-slate-400 ${inline ? "" : "block mb-0.5"}`}>{children}</label>;
}
